import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy, isDevMode, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';

import { AlertService } from 'src/app/services/alert.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { DataConventionsService } from 'src/app/services/data-conventions.service';
import { IOrganization } from 'src/app/models/organizations/organization';
import { OrganizationResult } from 'src/app/models/organizations/organization-result';
import { Subject, takeUntil } from 'rxjs';
import { OrganizationUpdateModel } from 'src/app/models/organizations/organization-update';
import { OrganizationUpdateProfileImage } from 'src/app/models/organizations/organization-update-profile-image';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import {
  DataConvention,
  dimensionOrderLabel,
} from 'src/app/models/organizations/data-convention';
import { OrganizationDataConvention } from 'src/app/models/organizations/organization-data-convention';
import {
  DataConventionSaveDialogComponent,
  DataConventionSaveDialogData,
} from 'src/app/components/home/data-conventions/data-convention-save-dialog/data-convention-save-dialog.component';

@Component({
    selector: 'app-organization-update',
    templateUrl: './organization-update.component.html',
    styleUrls: ['./organization-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class OrganizationUpdateComponent implements OnInit, OnDestroy {
  @Input() organization!: IOrganization;
  @Output() saved = new EventEmitter<IOrganization>();

  private destroy$ = new Subject();
  apiBase = environment.apiBase;
  loaded: boolean = false;
  organizationUpdateProfileImage: OrganizationUpdateProfileImage =
    new OrganizationUpdateProfileImage();
  organizationUpdate: OrganizationUpdateModel = new OrganizationUpdateModel();
  currentProfileImageUrl!: string | ArrayBuffer | null;
  file!: File;
  imageWidth: number = 400;
  imageHeight: number = 400;
  requestInProgress: boolean = false;
  validFile: boolean = false;
  nameFormControl: FormControl = new FormControl();
  shopifyFormControl: FormControl = new FormControl();
  profileImageFile!: string | ArrayBuffer | null;

  currentDataConvention: DataConvention | null = null;
  hasPriorConvention: boolean = false;
  readonly dimensionOrderLabel = dimensionOrderLabel;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private location: Location,
    private organizationsService: OrganizationsService,
    private dataConventionsService: DataConventionsService,
    private alertService: AlertService,
    private _alert: MatSnackBar,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    console.debug('Component: ', this.constructor.name);
    if (this.organization) {
      this.addExistingInfoToForm();
      this.addExistingImageToUI();
      this.loaded = true;
    } else {
      this.getOrganization();
    }
    this.loadLatestDataConvention();
  }

  loadLatestDataConvention(): void {
    const id: string = this.organization?.id ?? this.route.snapshot.paramMap.get('id')!;
    this.dataConventionsService
      .getLatestDataConvention(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((convention: DataConvention | null) => {
        this.currentDataConvention = convention;
        this.hasPriorConvention = convention !== null;
      });
  }

  openDataConventionDialog(): void {
    this.dialog
      .open(DataConventionSaveDialogComponent, { data: this.buildDialogData() })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: OrganizationDataConvention | undefined) => this.onDialogClosed(result));
  }

  private buildDialogData(): DataConventionSaveDialogData {
    return {
      organizationId: this.organization.id!,
      current: this.currentDataConvention,
      hasPriorConvention: this.hasPriorConvention,
    };
  }

  private onDialogClosed(result: OrganizationDataConvention | undefined): void {
    if (result) {
      this.loadLatestDataConvention();
    }
  }

  getOrganization(): void {
    const id: string = this.route.snapshot.paramMap.get('id')!;
    this.organizationsService
      .getOrganization(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: OrganizationResult) => {
        this.organization = result.organization;
        this.addExistingInfoToForm();
        this.addExistingImageToUI();
        this.loaded = true;
      });
  }
  addExistingInfoToForm() {
    this.nameFormControl.setValue(this.organization.name);
    this.shopifyFormControl.setValue(this.organization.shopifyHostDomain);
  }

  onFileChange(fileList: FileList | null): void {
    this.file = fileList![0];
    this.addFileToModel();
    this.extractImageFromFile();
    this.updateOrganizationProfileImage();
  }

  updateOrganizationProfileImage(): void {
    this.requestInProgress = true;
    // TODO pass through interceptor to remove request in progress
    this.organizationsService
      .updateOrganizationProfileImage(this.organization.id!, this.file!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (isDevMode()) {
            console.log('[DEV] [OrganizationUpdateComponent] result', result);
          }
          this.requestInProgress = false;
          this.sendAlert('Successful update', 3000, 'success');
        },
        error: () => {
          this.requestInProgress = false;
          this.sendAlert('Failed update', 3000, 'error');
        },
      });
  }

  updateOrganization(): void {
    this.requestInProgress = true;
    // TODO pass through interceptor to remove request in progress
    this.organizationUpdate.version = this.organization.version;
    this.organizationUpdate.name = this.nameFormControl.value;
    this.organizationUpdate.shopifyHostDomain = this.shopifyFormControl.value;
    if (isDevMode()) {
      console.log('[DEV] [OrganizationUpdateComponent] updating organization', this.organizationUpdate);
    }

    this.organizationsService
      .updateOrganization(this.organization.id!, this.organizationUpdate!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: OrganizationResult) => {
          if (isDevMode()) {
            console.log('[DEV] [OrganizationUpdateComponent] result', result);
          }
          this.requestInProgress = false;
          this.organization = result.organization;
          this.saved.emit(result.organization);
          this.alertService.successToast('Successful update');
        },
        error: () => {
          this.requestInProgress = false;
        },
      });
  }

  addFileToModel(): void {
    this.organizationUpdateProfileImage.file = this.file!;
    if (isDevMode()) {
      console.log('[DEV] [OrganizationUpdateComponent] this', this.organizationUpdateProfileImage.file);
    }
  }

  extractImageFromFile(): void {
    let fileReader: FileReader = new FileReader();
    fileReader.readAsDataURL(this.file!);
    fileReader.addEventListener(
      'load',
      () => {
        this.currentProfileImageUrl = fileReader.result;
        if (isDevMode()) {
          console.log('[DEV] [OrganizationUpdateComponent] this', this.currentProfileImageUrl);
        }
      },
      false,
    );
  }
  addExistingImageToUI(): void {
    this.currentProfileImageUrl = !!this.organization.profileImageUrl
      ? `${environment.apiBase}${this.organization.profileImageUrl}`
      : './assets/organization-placeholder.jpg';
  }

  sendAlert(message: string, duration: number, alertType: string): void {
    this._alert.open(message, 'close', {
      duration: duration,
      horizontalPosition: 'center',
      panelClass: [alertType],
    });
  }

  goBack(): void {
    this.router.navigate([`organizations`]);
  }

  shopifyLogin() {
    this.organizationsService.shopifyLogin(this.organization.id!).subscribe((result) => {
      console.debug('authorization login: ', result);
      window.open(result, '_blank');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
